/* eslint-disable */
import { formatDate } from './dateUtils';
import { devIsMovedToOrder, devLock, LOCK_LABEL } from './devLock';

// T-1039 — exported so DevConflictCard (and any future stage-themed surface)
// can theme its own UI without duplicating these hex values. Single source of
// truth: change a stage color HERE and it propagates to board cards, conflict
// modal dropdowns, etc.
export const STAGE_COLORS = {
  'Drawing stage':          { sash: '#c9a96e', light: '#f8f2e8', text: '#8a6820' },
  'Hand made sample stage': { sash: '#5b7fa6', light: '#eaf1f8', text: '#2d4f72' },
  'Color sample stage':     { sash: '#ceac92', light: '#faf0e4', text: '#84502e' },
};
export const DONE_COLOR = { sash: '#5a9e7c', light: '#eaf5ef', text: '#2d6648' };
const STOP_COLOR = { sash: '#8c7b8a', light: '#f4f0f4', text: '#5c4a5a' };
// Collection C2 — the locked development ("Moved to order") is APRICOT COPPER (Addendum B; ink read "off", Done owns green):
// plum collides with the Stopped mauve above (D1). Tested FIRST in every helper
// below; the lock itself is derived only in devLock.js (Conv #61).
export const LOCK_COLOR = { sash: '#dc9d74', light: '#fdf5ef', text: '#8a4f26' };   // apricot copper (Addendum B)
export const isMovedToOrder = devIsMovedToOrder;
const LEFT_STAGE_NUMBER = { _done: '04', 'Color sample': '03', 'Hand made': '02', Drawing: '01' };

// Brief P — unified Done + Completed detection. Aligns with the counts RPC at
// sql/dev-stage-counts-rpc-03-canonical-v2.sql (Brief I.5): a dev is treated as
// completed whenever ANY of these markers is set, covering both historical
// "Done" rows (stage_status='Done', no stop_reason) and modern V2-completed
// rows (stop_reason='completed', completion_stage set). Sibling helper at
// src/utils/devStatusFilter.js isDevDone must stay in lockstep (Convention
// #122 — single canonical detection across surfaces). Reads both projection-
// lifted top-level shape AND nested data.* shape (defensive).
export function isCompleted(dev) {
  if (!dev) return false;
  const s = (dev.full_status || dev.data?.full_status || '').toLowerCase();
  const stage = (dev.stage_status || dev.data?.stage_status || '').trim();
  const completedAt = dev.completedAt || dev.data?.completedAt;
  const stopReason = dev.stop_reason || dev.data?.stop_reason;
  return (
    stopReason === 'completed' ||
    stage === 'Done' ||
    completedAt != null ||
    s.includes('confirmed ✓') ||
    (s.includes('done') && !s.startsWith('stopped')) ||
    s.startsWith('completed at')
  );
}
export function isStopped(dev) {
  const s = (dev.full_status || '').toLowerCase();
  return s.startsWith('stopped') || s === 'cancelled' || s === 'paused';
}

export function getStageTheme(dev) {
  if (isMovedToOrder(dev)) return LOCK_COLOR;
  if (isStopped(dev))   return STOP_COLOR;
  if (isCompleted(dev)) return DONE_COLOR;
  return STAGE_COLORS[dev.stage_status] || STAGE_COLORS['Drawing stage'];
}

export function getStageLabel(dev) {
  if (isMovedToOrder(dev)) return LOCK_LABEL;
  if (isStopped(dev))   return 'Stopped';
  if (isCompleted(dev)) return 'Completed ✓';
  const map = {
    'Drawing stage':          'Drawing Stage',
    'Hand made sample stage': 'Hand Made Stage',
    'Color sample stage':     'Color Sample Stage',
  };
  return map[dev.stage_status] || (dev.stage_status || '—');
}

export function getStageNumber(dev) {
  if (isMovedToOrder(dev)) return LEFT_STAGE_NUMBER[devLock(dev)?.leftStage] || '—';   // the stage it LEFT
  if (isStopped(dev))   return '—';
  if (isCompleted(dev)) return '04';
  const map = { 'Drawing stage':'01', 'Hand made sample stage':'02', 'Color sample stage':'03' };
  return map[dev.stage_status] || '—';
}

export function getStageIcon(dev) {
  if (isMovedToOrder(dev)) return 'lock';
  if (isStopped(dev))   return 'stop';
  if (isCompleted(dev)) return 'check';
  if (dev.stage_status === 'Drawing stage')          return 'pencil';
  if (dev.stage_status === 'Hand made sample stage') return 'hand';
  return 'palette';
}

export function formatDevNumber(dev) {
  return (dev.dev_number || '').replace(/-/g, '');
}

export function getTypeColor(dev) {
  const prefix = (dev.dev_number || '').toUpperCase().replace(/-/g,'').slice(0, 2);
  if (prefix === 'DA') return '#a07828';
  if (prefix === 'DS') return '#2a5888';
  if (prefix === 'DO') return '#287850';
  return 'var(--sandstone)';
}

// formatDate re-exported from dateUtils.js for backward compatibility
export { formatDate };

export function getDaysInStage(dev) {
  const startedAt = dev.stageStartedAt || dev.data?.stageStartedAt || dev.data?.created_at || dev.created_at;
  if (!startedAt) return null;
  const completedAt = dev.completedAt || dev.data?.completedAt || dev.data?.completed_at;
  const end = completedAt ? new Date(completedAt) : new Date();
  const ms = end.getTime() - new Date(startedAt).getTime();
  return Math.max(1, Math.floor(ms / 86400000));
}

export function getShapes(dev) {
  return [...new Set([
    dev.shape1, dev.data?.shape1,
    dev.shape2, dev.data?.shape2,
    dev.shape3, dev.data?.shape3,
    dev.shape,  dev.data?.shape,
  ].filter(Boolean))];
}

/**
 * Returns the URL of the dev's primary image, respecting data.primaryImageIndex
 * with fallback to images[0]. Handles both top-level images[] and data.images[]
 * storage shapes; tolerates string or {url} entries; returns null if no images.
 */
export function getDevPrimaryImage(dev) {
  if (!dev) return null;
  const rawImgs = (dev.images?.length ? dev.images : dev.data?.images) || [];
  if (rawImgs.length === 0) return null;
  const pidx = dev.data?.primaryImageIndex ?? dev.primaryImageIndex ?? 0;
  const img = (pidx > 0 && pidx < rawImgs.length) ? rawImgs[pidx] : rawImgs[0];
  return typeof img === 'string' ? img : (img?.url || null);
}

/**
 * The {kind, id, code, name, image} seed an announcement carries for a dev.
 *
 * Lives here, beside getDevPrimaryImage, because it reads the CARD's shape --
 * the flattened {...d, ...d.data} object useAppData builds -- where the dev
 * number may sit at either level. attachmentRegistry.js answers the same
 * question for a row fetched from PostgREST (`data->>dev_number`), a DIFFERENT
 * shape, so the two must not be merged: reusing the registry here would read
 * undefined and attach a nameless record.
 */
export function devAnnounceSeed(dev) {
  return {
    kind: 'dev',
    id: String(dev.id),
    code: dev.dev_number || dev.data?.dev_number || '',
    name: dev.data?.designer || dev.designer || '',
    image: getDevPrimaryImage(dev),
  };
}

export function getBoData(dev, catalogItems) {
  const bo  = dev.based_on || dev.data?.based_on;
  const bod = dev.based_on_data || dev.data?.based_on_data;
  if (!bo) return null;
  let name = '', image = null, ref = '';
  if (bo === 'Original Item (Catalog)') {
    const item = bod?.item_id ? (catalogItems || []).find(i => i.id === bod.item_id) : null;
    name  = item?.data?.brand || item?.brand || bod?.brand || 'Originals Item';
    image = (item?.images || [])[0] || bod?.ref_image || null;
    ref   = item?.data?.sku || item?.sku || bod?.sku || '';
  } else if (bo === 'Image / Fty Sample' || bo === 'Brand Image / Reference') {
    // T-1051 — Image / Fty Sample dispatches on bod.sub_type:
    //   'brand' → brand_name (canonical label)
    //   'fty'   → factory_name (new label, post-T-1051)
    //   undefined → legacy-tolerance default to 'brand'. Pre-T-1051 rows had
    //   based_on='Brand Image / Reference' + no sub_type; sql/dev-based-on-01
    //   stamps them with sub_type='brand'. The OR clause above + this default
    //   keep card render correct between SQL exec and frontend deploy.
    const subType = bod?.sub_type || 'brand';
    name  = subType === 'fty' ? (bod?.factory_name || 'Fty Sample') : (bod?.brand_name || 'Brand Ref');
    image = bod?.ref_image || null;
    // T-1016 Phase D — display fallback for legacy multi-image rows where the
    // BASED ON image was orphaned into dev.images (corruption from pre-T-013
    // paste handler). When ref_image is empty AND the dev has images, surface
    // images[0] as a best-guess display. Note: ~50% of these may show the
    // wrong image (e.g. DA3148 has technical drawing at images[0]). Self-heals
    // when user edits and uploads correct ref_image (becomes non-empty).
    // Mirrored in src/components/developments/devDetail/hooks/useDevDetail.js —
    // keep both in sync.
    if (!image) {
      const imgs = (dev.images?.length ? dev.images : dev.data?.images) || [];
      if (imgs.length > 0) {
        const first = imgs[0];
        image = typeof first === 'string' ? first : (first?.url || null);
      }
    }
    ref   = bod?.ref_code || '';
  } else if (bo === 'Store Model (CL)') {
    name  = bod?.cl_number || 'CL Model';
    image = bod?.ref_image || null;
  } else if (bo === 'Other') {
    // T-1051 — Other now stores bod.images = [{url, name}, ...] (multi-image)
    // in addition to legacy bod.description. Use images[0].url as primary for
    // board card thumbnail fallback. Legacy rows (description-only, no images
    // array) fall back to bod.ref_image (always null pre-T-1051 for Other,
    // but preserved for defensive symmetry with the other branches).
    name  = bod?.description?.slice(0, 24) || bo;
    const otherImgs = Array.isArray(bod?.images) ? bod.images : [];
    image = (otherImgs[0]?.url) || bod?.ref_image || null;
  } else {
    name  = bod?.description?.slice(0, 24) || bo;
    image = bod?.ref_image || null;
  }
  return { name, image, ref };
}
